/// <amd-module name="Repos/Views/Settings/Repositories-Policies/Components/PolicyInput" />
import React = require("react");
import { InputType, IPolicy, PolicyInputInheritanceType, SelectableItem } from "Repos/Views/Settings/Repositories-Policies/Contracts/Contracts";
import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Dropdown } from "VSSUI/Dropdown";
import { FormItem } from "VSSUI/FormItem";
import { Icon } from "VSSUI/Icon";
import { IListSelection } from "VSSUI/List";
import { IListBoxItem } from "VSSUI/ListBox";
import { TextField, TextFieldWidth } from "VSSUI/TextField";
import { css, KeyCode } from "VSSUI/Util";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { InheritedValuesTable } from "./InheritedValuesTable";
import { PolicyInputCheckbox } from "./PolicyInputCheckbox";
import { PolicyInputCheckboxTriggeredRadioGroup } from "./PolicyInputCheckboxTriggeredRadioGroup";
import { PolicyInputRadioGroup } from "./PolicyInputRadioGroup";

interface IPolicyInputProps {
    className?: string;
    policy: IPolicy;
    onAction: (value?: string, valueObj?: { [key: string]: boolean }) => void;
}

/**
 * Generic component for all possible policy settings inputs.
 */
export class PolicyInput extends VssComponent<IPolicyInputProps> {
    private errorMsg: IObservableValue<string>;
    private errorShow: IObservableValue<boolean>;

    // Since policy object contains separate properties for a single settings (such as a number value) and multiple settings (such as a list of booleans),
    // this class handles them in separate objects.

    // State for keeping track of list selection. Maps settings keys to boolean which marks if the option is selected.
    private multiSelection: { [id: string]: IObservableValue<boolean> };
    // State for keeping track of dropdown selection.
    private selection: IListSelection;
    // State for keeping track of text/number field and radio buttons.
    private value: IObservableValue<string>;

    public constructor(props: IPolicyInputProps, context: IVssComponentContext) {
        super(props, context);
        this.errorMsg = new ObservableValue<string>("");
        this.errorShow = new ObservableValue<boolean>(false);
        this.value = new ObservableValue<string>(props.policy.inheritedSettingValue || props.policy.settingValue || "");
        this.selection = new DropdownSelection();

        if (props.policy.input && props.policy.input.type === InputType.List) {
            this.selectInitialDropdownValue();
        }

        if (
            props.policy.input &&
            props.policy.input.options &&
            props.policy.settingValueObject &&
            (props.policy.input.type === InputType.MultiSelect || props.policy.input.type === InputType.MultiSelectWithRadioButtonsAndText)
        ) {
            this.multiSelection = {};
            this.getAllOptions().forEach(item => {
                let isItemChecked;
                if (props.policy.input?.inputInheritanceType === PolicyInputInheritanceType.ReverseInheritance) {
                    isItemChecked = !!props.policy.inheritedSettingValueObject
                        ? !!props.policy.inheritedSettingValueObject[item.id] && !!props.policy.settingValueObject![item.id]
                        : !!props.policy.settingValueObject![item.id];
                } else {
                    isItemChecked =
                        !!props.policy.settingValueObject![item.id] ||
                        (!!props.policy.inheritedSettingValueObject && !!props.policy.inheritedSettingValueObject[item.id]);
                }

                this.multiSelection[item.id] = new ObservableValue<boolean>(isItemChecked);
            });
        }
    }

    public render = (): JSX.Element => {
        const pol = this.props.policy;
        const input = pol.input;

        return input ? (
            <div className={css("flex-column", "rhythm-vertical-16", !input.fullWidth ? "policy-input-restrict-width" : "policy-input-full-width")}>
                {input.showInheritedValuesTable && (pol.activeInheritedAndEnterprisePolicies || pol.additonalCurrentScopePolicies) && (
                    <InheritedValuesTable policy={pol} />
                )}
                <div className={this.props.className}>
                    <div className="flex-row">
                        <FormItem message={this.errorMsg} error={this.errorShow}>
                            {(input.type === InputType.Text ||
                                input.type === InputType.Number ||
                                input.type === InputType.MultiSelectWithRadioButtonsAndText) && (
                                <TextField
                                    ariaLabel={input.type === InputType.MultiSelectWithRadioButtonsAndText ? undefined : pol.title}
                                    value={this.value}
                                    onChange={this.onChange}
                                    onKeyPress={this.onKeyDown}
                                    onBlur={this.validateAndSave}
                                    width={TextFieldWidth.standard}
                                    tooltipProps={{ text: pol.tooltip }}
                                    label={input.type === InputType.MultiSelectWithRadioButtonsAndText ? input.title : undefined}
                                    disabled={pol.disabled}
                                />
                            )}
                            {input.type === InputType.List && (
                                <Dropdown
                                    ariaLabel={pol.title}
                                    className={TextFieldWidth.standard}
                                    items={input.options!}
                                    onSelect={(event, listBoxItem) => this.onSelect(listBoxItem.id)}
                                    selection={this.selection}
                                    disabled={pol.disabled}
                                />
                            )}
                            {input.type === InputType.RadioList && (
                                <PolicyInputRadioGroup
                                    options={input.options}
                                    isDisabled={!!pol.disabled || !!input.disabled}
                                    initialSelectedId={((input.options as SelectableItem[]) || []).find(item => item.value === this.value?.value)?.id}
                                    onSelect={selectedId => {
                                        const selectedValue = (input.options as SelectableItem[] | undefined)?.find(item => item.id === selectedId)
                                            ?.value;
                                        selectedValue && this.onSelect(selectedValue);
                                    }}
                                />
                            )}
                            {(input.type === InputType.MultiSelect || input.type === InputType.MultiSelectWithRadioButtonsAndText) &&
                                this.multiSelection && (
                                    <div>
                                        <div>
                                            {input.title && input.type !== InputType.MultiSelectWithRadioButtonsAndText && (
                                                <span className="body-s font-weight-heavy secondary-text">{input.title}</span>
                                            )}
                                        </div>
                                        <div
                                            className={css(
                                                "policy-input-checkbox-container",
                                                !input.singleColumnRender ? "flex-row flex-wrap" : "flex-column"
                                            )}
                                        >
                                            {this.getOptionsWithoutSubOptions().map(item => (
                                                <PolicyInputCheckbox
                                                    key={item.id}
                                                    item={item}
                                                    isChecked={this.multiSelection[item.id]}
                                                    isDisabled={
                                                        !!(
                                                            pol.disabled ||
                                                            input.disabled ||
                                                            item.disabled ||
                                                            (input.inputInheritanceType === PolicyInputInheritanceType.ReverseInheritance &&
                                                                pol.inheritedSettingValueObject &&
                                                                !pol.inheritedSettingValueObject[item.id]) ||
                                                            ((input.inputInheritanceType === PolicyInputInheritanceType.Default ||
                                                                !input.inputInheritanceType) &&
                                                                pol.inheritedSettingValueObject &&
                                                                pol.inheritedSettingValueObject[item.id])
                                                        )
                                                    }
                                                    onChange={value => this.onChangeBool(value, item.id)}
                                                />
                                            ))}
                                            {this.getOptionsWithSubOptions().map(item => (
                                                <PolicyInputCheckboxTriggeredRadioGroup
                                                    key={item.id}
                                                    item={item}
                                                    isDisabled={
                                                        !!(
                                                            pol.disabled ||
                                                            input.disabled ||
                                                            item.disabled ||
                                                            (input.inputInheritanceType === PolicyInputInheritanceType.ReverseInheritance &&
                                                                pol.inheritedSettingValueObject &&
                                                                !pol.inheritedSettingValueObject[item.id]) ||
                                                            ((input.inputInheritanceType === PolicyInputInheritanceType.Default ||
                                                                !input.inputInheritanceType) &&
                                                                pol.inheritedSettingValueObject &&
                                                                pol.inheritedSettingValueObject[item.id])
                                                        )
                                                    }
                                                    policy={pol}
                                                    optionToSelectionMap={this.multiSelection}
                                                    onChange={this.onChangeMultipleBools}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                        </FormItem>
                        {getTooltipIcon(pol.tooltip)}
                    </div>
                </div>
            </div>
        ) : (
            <></>
        );
    };

    private getOptionsWithoutSubOptions = (): SelectableItem[] => {
        const options = this.props.policy.input!.options as SelectableItem[] | undefined;
        if (options) {
            return options.filter(o => !o.subOptions);
        }
        return [];
    };

    private getOptionsWithSubOptions = (): SelectableItem[] => {
        const options = this.props.policy.input!.options as SelectableItem[] | undefined;
        if (options) {
            return options.filter(o => o.subOptions);
        }
        return [];
    };

    private getAllOptions = (): SelectableItem[] => {
        const inputOptions = this.props.policy.input!.options as SelectableItem[] | undefined;
        const items: SelectableItem[] = [];
        if (inputOptions) {
            inputOptions.forEach(item => {
                if (item.subOptions) {
                    items.push(...item.subOptions);
                } else {
                    items.push(item);
                }
            });
        }
        return items;
    };

    /**
     * Creates and returns policy settings object from the current state.
     */
    private getSettingValueObj = (): { [key: string]: boolean } | undefined => {
        if (this.multiSelection) {
            const settingValueObj: { [key: string]: boolean } = {};
            const items = this.getAllOptions();
            items.forEach(item => {
                if (this.multiSelection[item.id].value) {
                    settingValueObj[item.id] = true;
                } else if (this.props.policy.input!.useFalseOnDeselect) {
                    settingValueObj[item.id] = false;
                }
            });
            return settingValueObj;
        }
        return undefined;
    };

    private onChange = (event: any, value: string) => {
        if (this.props.policy.input!.type === InputType.Number || this.props.policy.input!.type === InputType.MultiSelectWithRadioButtonsAndText) {
            this.value.value = value.trim();
        } else {
            this.value.value = value;
        }
    };

    private onChangeBool = (value: boolean, itemId: string) => {
        this.multiSelection[itemId].value = value;
        this.validateAndSave();
    };

    /**
     * Updates multiple values in policy settings and saves them in one call.
     */
    private onChangeMultipleBools = (changes: { [itemId: string]: boolean }) => {
        Object.keys(changes).forEach(itemId => {
            this.multiSelection[itemId].value = changes[itemId];
        });
        this.validateAndSave();
    };

    private onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (event.keyCode === KeyCode.enter) {
            this.validateAndSave();
        }
    };

    private onSelect = (value: string) => {
        this.value.value = value;
        this.validateAndSave();
    };

    private selectInitialDropdownValue = () => {
        const valueToShow = this.props.policy.inheritedSettingValue || this.props.policy.settingValue;
        let index = this.props.policy.input!.options!.findIndex((value: IListBoxItem) => {
            return value.id === valueToShow;
        });

        if (index < 0) {
            index = 0;
        }
        this.selection.select(index);
        this.validate();
    };

    private validateAndSave = () => {
        const settingValueObj = this.getSettingValueObj();
        this.validate(settingValueObj);

        if (!this.errorShow.value) {
            this.props.onAction(this.value.value, settingValueObj);
        }
    };

    private validate = (settingValueObj?: { [key: string]: boolean }) => {
        const validations = this.props.policy.input!.validations;
        if (validations) {
            for (let i = 0; i < validations.length; i++) {
                const msg = validations[i](this.value.value, this.props.policy, settingValueObj!);
                if (msg) {
                    this.errorMsg.value = msg;
                    this.errorShow.value = true;
                    break;
                } else {
                    this.errorMsg.value = "";
                    this.errorShow.value = false;
                }
            }
        }
    };
}

function getTooltipIcon(tooltip?: string) {
    return tooltip ? (
        <span>
            <span className="icon-margin"></span>
            <span className="policy-toggle-input-icon">
                <Icon ariaLabel="Information Tooltip" iconName="Info" tooltipProps={{ text: tooltip }} />
            </span>
        </span>
    ) : (
        <></>
    );
}
